import { formatDate, formatDateTime } from '@/lib/date'
import { money } from '@/lib/money'
import type { OrderEvent } from '@/types'

const eventLabels: Record<string, string> = {
  edited: 'Details edited',
  rescheduled: 'Production date moved',
  replanned: 'Re-planned',
  proceeded: 'Sent to production',
  pending_remark: 'Remark (pending)',
  cancelled: 'Cancelled',
  expired: 'Cancelled - payment not completed in time',
  restored: 'Restored - payment arrived',
  payment_recorded: 'Payment recorded',
  refund_recorded: 'Refund recorded',
  paid_after_cancel: 'Paid after it was cancelled',
}

// Also the display order: jsonb does not keep the order fields were written in.
const fieldLabels: Record<string, string> = {
  job_name: 'Job name',
  customer_ref: 'Customer ref',
  notes: 'Notes',
  pic: 'PIC',
  artwork: 'Artwork',
  font: 'Font file',
  production_date: 'Production date',
  expected_completion_date: 'Delivery due',
  status: 'Status',
  payment_status: 'Payment',
  refunded_amount: 'Refunded',
}
const fieldOrder = Object.keys(fieldLabels)

function show(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '(blank)'
  if (field === 'production_date' || field === 'expected_completion_date') return formatDate(String(value))
  if (field === 'refunded_amount') return money(Number(value))
  if (field === 'status' || field === 'payment_status') return String(value).replace(/_/g, ' ')
  return String(value)
}

/** Who changed what on an order after it was placed, newest first. */
export default function OrderHistory({ events }: { events: OrderEvent[] }) {
  if (events.length === 0) {
    return <p className="text-xs text-muted">Nothing has changed since the order was placed.</p>
  }

  return (
    <ol className="space-y-3">
      {[...events].reverse().map((event, i) => {
        const changes = Object.entries(event.changes ?? {}).sort(
          ([a], [b]) => fieldOrder.indexOf(a) - fieldOrder.indexOf(b),
        )

        return (
          <li key={i} className="text-sm">
            <p className="font-semibold">{eventLabels[event.type] ?? event.type.replace(/_/g, ' ')}</p>
            <p className="text-xs text-muted">
              {formatDateTime(event.at)} by {event.actor ?? 'the system'}
            </p>
            {changes.map(([field, [from, to]]) => (
              <p key={field} className="mt-0.5 break-words text-xs">
                <span className="text-muted">{fieldLabels[field] ?? field}:</span> {show(field, from)}{' '}
                <span className="text-muted">to</span> {show(field, to)}
              </p>
            ))}
            {event.note && <p className="mt-0.5 break-words text-xs italic">{event.note}</p>}
          </li>
        )
      })}
    </ol>
  )
}
